import React, { useState } from 'react';
import { usePageFilter } from '../../contexts/PageFilterContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Check, X, BookOpen, Trash2, Calendar } from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';

const DATE_PRESETS = [
  { label: '1W', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
];

function getPresetRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
}

export function PageFilterTabs() {
  const { branchPages, selectedPageId, setSelectedPageId, dateRange, setDateRange, fetchBranchPages } = usePageFilter();
  const [showAddPage, setShowAddPage] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  const handlePreset = (preset) => {
    if (activePreset === preset.label) {
      setActivePreset(null);
      setDateRange({ startDate: null, endDate: null });
    } else {
      setActivePreset(preset.label);
      setShowCustomRange(false);
      setDateRange(getPresetRange(preset.days));
    }
  };

  const handleCustomToggle = () => {
    if (showCustomRange) {
      setShowCustomRange(false);
    } else {
      setShowCustomRange(true);
      setActivePreset(null);
    }
  };

  const clearDateFilter = () => {
    setDateRange({ startDate: null, endDate: null });
    setActivePreset(null);
    setShowCustomRange(false);
  };

  const hasDateFilter = dateRange.startDate || dateRange.endDate;

  const handleAddPage = async () => {
    if (!newPageName.trim()) return;
    try {
      await axios.post('/branch-pages', { name: newPageName.trim() });
      toast.success('Page created!');
      setNewPageName('');
      setShowAddPage(false);
      fetchBranchPages();
    } catch (error) {
      toast.error('Failed to create page');
    }
  };

  const handleDeletePage = async (pageId) => {
    try {
      await axios.delete(`/branch-pages/${pageId}`);
      toast.success('Page deleted');
      if (selectedPageId === pageId) setSelectedPageId(null);
      setDeleteConfirm(null);
      fetchBranchPages();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="global-page-filter">
      {/* ALL button */}
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setSelectedPageId(null)}
        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all relative overflow-hidden ${
          selectedPageId === null
            ? 'bg-gradient-to-r from-[#9945FF] to-[#00F5FF] text-black shadow-lg shadow-[#00F5FF]/20'
            : 'bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5 hover:border-white/20'
        }`}
        data-testid="global-page-filter-all"
      >
        {selectedPageId === null && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-[#9945FF]/50 to-[#00F5FF]/50 blur-xl"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        )}
        <span className="relative flex items-center gap-1.5">
          <BookOpen className="w-4 h-4" /> ALL
        </span>
      </motion.button>
      
      {/* Page tabs with delete */}
      {branchPages.map((pg) => (
        <div key={pg.id} className="relative group flex items-center">
          {deleteConfirm === pg.id ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[#1a1a2e] border border-red-500/30"
            >
              <span className="text-xs text-red-400 mr-1">Delete?</span>
              <button
                onClick={() => handleDeletePage(pg.id)}
                className="px-2 py-1 rounded-lg bg-red-500/20 text-red-400 text-xs font-bold hover:bg-red-500/40"
                data-testid={`confirm-delete-page-${pg.id}`}
              >Yes</button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-2 py-1 rounded-lg bg-white/5 text-gray-400 text-xs hover:bg-white/10"
              >No</button>
            </motion.div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setSelectedPageId(pg.id)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all relative overflow-hidden ${
                selectedPageId === pg.id
                  ? 'text-black shadow-lg'
                  : 'bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5 hover:border-white/20'
              }`}
              style={selectedPageId === pg.id ? { 
                background: pg.color || '#00D4FF',
                boxShadow: `0 0 20px ${pg.color || '#00D4FF'}40`
              } : {}}
              data-testid={`global-page-filter-${pg.id}`}
            >
              {selectedPageId === pg.id && (
                <motion.div
                  className="absolute inset-0 blur-xl"
                  style={{ background: pg.color || '#00D4FF' }}
                  animate={{ opacity: [0.2, 0.4, 0.2] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              )}
              <span className="relative">{pg.name}</span>
            </motion.button>
          )}
          {deleteConfirm !== pg.id && (
            <button
              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(pg.id); }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500/80 text-white items-center justify-center text-[10px] hidden group-hover:flex hover:bg-red-500 z-10"
              data-testid={`delete-page-${pg.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      
      {/* Add page */}
      <AnimatePresence>
        {showAddPage ? (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="flex items-center gap-2 overflow-hidden"
          >
            <Input
              value={newPageName}
              onChange={(e) => setNewPageName(e.target.value)}
              placeholder="Page name..."
              className="h-9 w-36 bg-[#1a1a2e] border-white/10 text-white text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleAddPage()}
              autoFocus
              data-testid="new-page-name-input"
            />
            <Button size="sm" onClick={handleAddPage} className="h-9 bg-[#00FF88] text-black hover:bg-[#00FF88]/80" data-testid="confirm-add-page">
              <Check className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowAddPage(false); setNewPageName(''); }} className="h-9 text-gray-400">
              <X className="w-4 h-4" />
            </Button>
          </motion.div>
        ) : (
          <motion.button
            whileHover={{ scale: 1.1, boxShadow: '0 0 15px #00F5FF40' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddPage(true)}
            className="px-3 py-2 rounded-xl text-sm font-bold bg-[#0d0d15] text-[#00F5FF] border border-dashed border-[#00F5FF]/30 hover:border-[#00F5FF]/60 transition-all"
            data-testid="add-page-btn"
          >
            <Plus className="w-4 h-4" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Separator */}
      <div className="w-px h-8 bg-white/10 mx-1" />

      {/* Date Range Filter */}
      <div className="flex items-center gap-1.5">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => handlePreset(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activePreset === p.label
                ? 'bg-[#00F5FF] text-black shadow-lg shadow-[#00F5FF]/20'
                : 'bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5 hover:border-white/20'
            }`}
            data-testid={`date-preset-${p.label}`}
          >{p.label}</button>
        ))}
        <button
          onClick={handleCustomToggle}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
            showCustomRange
              ? 'bg-[#9945FF] text-white shadow-lg shadow-[#9945FF]/20'
              : 'bg-[#0d0d15] text-gray-400 hover:text-white border border-white/5 hover:border-white/20'
          }`}
          data-testid="date-custom-btn"
        >
          <Calendar className="w-3.5 h-3.5" /> Custom
        </button>
        {hasDateFilter && (
          <button
            onClick={clearDateFilter}
            className="p-1.5 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all"
            data-testid="clear-date-filter"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Custom date range inputs */}
      {showCustomRange && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateRange.startDate || ''}
            onChange={(e) => { setActivePreset(null); setDateRange(prev => ({ ...prev, startDate: e.target.value || null })); }}
            className="h-8 px-2 rounded-lg bg-[#0d0d15] border border-white/10 text-white text-xs focus:border-[#9945FF]/50 outline-none [color-scheme:dark]"
            data-testid="date-range-start"
          />
          <span className="text-gray-500 text-xs">to</span>
          <input
            type="date"
            value={dateRange.endDate || ''}
            onChange={(e) => { setActivePreset(null); setDateRange(prev => ({ ...prev, endDate: e.target.value || null })); }}
            className="h-8 px-2 rounded-lg bg-[#0d0d15] border border-white/10 text-white text-xs focus:border-[#9945FF]/50 outline-none [color-scheme:dark]"
            data-testid="date-range-end"
          />
        </div>
      )}
    </div>
  );
}
